import { Box, Card, Container, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import {
  IconChartBar,
  IconEngine,
  IconGitFork,
  IconShieldCheckFilled,
  IconUsers,
  type Icon,
} from '@tabler/icons-react';

import { Reveal } from '@/components/motion/Reveal';
import classes from './SocialProof.module.css';

interface Stat {
  value: string;
  label: string;
  detail: string;
  icon: Icon;
  accent: 'forge' | 'safety' | 'ai';
}

const STATS: Stat[] = [
  {
    value: '13',
    label: 'Supported platforms',
    detail:
      'LS1/2/3, K20A, K24A, EJ205/257, FA20DIT, 2JZ-GTE, B58, 4G63T, 4B11T, L3-VDT — each with a vetted rule pack and base maps.',
    icon: IconEngine,
    accent: 'forge',
  },
  {
    value: '126',
    label: 'Tests in the suite',
    detail:
      'Every safety rule, library route, and community endpoint has integration tests. The deterministic core has been hammered before the AI ever sees a map.',
    icon: IconShieldCheckFilled,
    accent: 'safety',
  },
  {
    value: '3',
    label: 'Experience modes',
    detail:
      'Apprentice, Tuner, and Pro — the editor adjusts guard rails and explanations to match. New tuners get hand-holding; veterans get out of the way.',
    icon: IconUsers,
    accent: 'ai',
  },
];

interface Pillar {
  icon: Icon;
  title: string;
  body: string;
}

const COMMUNITY_PILLARS: Pillar[] = [
  {
    icon: IconGitFork,
    title: 'Fork &amp; remix',
    body: 'Every shared tune is forkable. Build on what works, attribute the original, and keep the safety verdict attached.',
  },
  {
    icon: IconChartBar,
    title: 'Verified ratings',
    body: 'Stars and reviews come from tuners who actually downloaded — not drive-by spam. Each rating shows the safety tier it was tested at.',
  },
  {
    icon: IconShieldCheckFilled,
    title: 'Safety verdicts attached',
    body: 'Library tunes are graded by the same deterministic engine that runs in your editor. No surprises after you flash.',
  },
];

const ACCENT: Record<Stat['accent'], { color: string; bg: string; ring: string }> = {
  forge: { color: 'var(--tf-forge-start)', bg: 'rgba(255, 107, 44, 0.10)', ring: 'rgba(255, 107, 44, 0.35)' },
  safety: { color: 'var(--tf-safety)', bg: 'rgba(0, 230, 118, 0.08)', ring: 'rgba(0, 230, 118, 0.30)' },
  ai: { color: 'var(--tf-ai)', bg: 'rgba(187, 134, 252, 0.10)', ring: 'rgba(187, 134, 252, 0.30)' },
};

export function SocialProof() {
  return (
    <Box
      component="section"
      id="social-proof"
      py={{ base: 64, md: 96 }}
      aria-labelledby="proof-title"
    >
      <Container size="xl">
        <Stack align="center" ta="center" gap="md" mb="xl">
          <Reveal>
            <Text size="sm" tt="uppercase" lts="0.12em" c="var(--tf-forge-start)" fw={700}>
              By the numbers
            </Text>
          </Reveal>
          <Reveal delay={0.05}>
            <Title id="proof-title" order={2} className={classes.heading}>
              Engineered first. Marketed second.
            </Title>
          </Reveal>
          <Reveal delay={0.1}>
            <Text c="var(--tf-text-secondary)" maw={620}>
              Clancy Tuning is open about what it ships and what it doesn’t. Here are the numbers
              behind v1.0 — and the community pillars that hold them up.
            </Text>
          </Reveal>
        </Stack>

        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
          {STATS.map((s, idx) => {
            const a = ACCENT[s.accent];
            const Icon = s.icon;
            return (
              <Reveal key={s.label} delay={idx * 0.06}>
                <Card className={classes.card} p="xl" h="100%" withBorder radius="lg">
                  <Stack gap="md" h="100%">
                    <Box
                      aria-hidden="true"
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 10,
                        background: a.bg,
                        border: `1px solid ${a.ring}`,
                        color: a.color,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon size={22} stroke={1.8} />
                    </Box>
                    <Box>
                      <Text
                        fz={{ base: 40, md: 48 }}
                        fw={800}
                        lh={1}
                        style={{ color: a.color, letterSpacing: '-0.03em' }}
                      >
                        {s.value}
                      </Text>
                      <Text
                        size="xs"
                        tt="uppercase"
                        lts="0.10em"
                        fw={700}
                        c="var(--tf-text-primary)"
                        mt={6}
                      >
                        {s.label}
                      </Text>
                    </Box>
                    <Text size="sm" c="var(--tf-text-secondary)" lh={1.55}>
                      {s.detail}
                    </Text>
                  </Stack>
                </Card>
              </Reveal>
            );
          })}
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg" mt={48}>
          {COMMUNITY_PILLARS.map((p, idx) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.title} delay={0.15 + idx * 0.05}>
                <Box>
                  <Box
                    aria-hidden="true"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: 'rgba(255, 107, 44, 0.10)',
                      border: '1px solid rgba(255, 107, 44, 0.30)',
                      color: 'var(--tf-forge-start)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <Icon size={18} stroke={1.8} />
                  </Box>
                  <Text fw={700} c="var(--tf-text-primary)" mb={4}>
                    {p.title.replace(/&amp;/g, '&')}
                  </Text>
                  <Text size="sm" c="var(--tf-text-secondary)" lh={1.55}>
                    {p.body}
                  </Text>
                </Box>
              </Reveal>
            );
          })}
        </SimpleGrid>
      </Container>
    </Box>
  );
}
